import * as ng from 'angular';
import * as _ from 'underscore';

import {ReportsService, Dataset, DatasetField} from '.';

/***
   Copyright 2013 Teun Duynstee

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
   */
var firstBy = (function() {
   /* mixin for the `thenBy` property */
   function extend(f) {
      f.thenBy = tb;
      return f;
   }
   /* adds a secondary compare function to the target function (`this` context)
      which is applied in case the first one returns 0 (equal)
      returns a new compare function, which has a `thenBy` method as well */
   function tb(y) {
      var x = this;
      return extend(function(a, b) {
         return x(a,b) || y(a,b);
      });
   }
   return extend;
})();

ng.module('e360')
   .factory('reportTableFactory', reportTableFactory);

reportTableFactory.$inject = ['instanceServiceFactory'];
function reportTableFactory(instanceServiceFactory) {
   return instanceServiceFactory(ReportTableService);
}

/*
 * Report table code was mixed in with a directive for creating report dataset
 * "widgets" including charts. There was a bit of a mess of code with some
 * table code in the directive and some in a service. This is an attempt to
 * put the table code in one place, with a dedicated directive/service.
 * There is still lots of room for improvement:
 * - header/row/sub-row/footer field lists could be generated in constructor (optimization)
 * - sub-row expansion is weird, why not have the view iterate the subrows
 *   instead of injecting the rows into the current page of rows?
 * - column summaries are crazy inefficient, they are calculated each digest
 *   cycle but could be calculated when data is loaded and saved
 * - column summary on grouped data doesn't work on string columns where
 *   sub-rows have many different values for the column
 * - consider modeling rows, fields, and the current page for better encapsulation
 */

export class ReportTableService {
   hasSums:boolean = false;

   currentPage:number = 1;
   rowsPerPage:number = 15;

   pageRows:any[] = [];
   pageRowCount:number;
   data:any[];

   private sortFields:DatasetField[];

   static $inject = ['ReportsService', '$injector', '$q', 'ds'];

   constructor(
      private reportsService:ReportsService,
      private $injector:ng.auto.IInjectorService,
      private $q:ng.IQService,
      private ds:Dataset
   ) {
      this.hasSums = _.any(ds.fields, f => f.sum);

      // some datasets are pre-sorted, save the initial sorting state
      this.sortFields = _.filter(ds.fields, f => f.sort != null);
   }

   get isGrouped():boolean {
      return this.ds.type === 'table-grouped';
   }

   get page() {
      return this.currentPage;
   }

   set page(val:number) {
      this.currentPage = val;
      this.setPageRows();
   }

   private setPageRows() {
      let start = (this.page - 1) * this.rowsPerPage;
      let end = start + this.rowsPerPage;

      // row data deep copy so that paging resets expanded state
      this.pageRows = ng.copy(this.data.slice(start, end));

      // save the length to reflect number of rows in the page, which
      // should stay fixed when rows are expanded
      this.pageRowCount = this.pageRows.length;

      if (this.ds.type === 'table-expanded') {
         this.expandAll();
      }
   }

   getFieldsForRow(row) {
      if (row.isSubrow) {
         return _.reject(this.ds.fields, f => f.excludeInSubRows || f.hidden);
      }

      return _.reject(this.ds.fields, f => f.excludeInParentRows || f.hidden);
   }

   get headerFields() {
      return _.reject(this.ds.fields, f => f.excludeInHeader || f.hidden);
   }

   get superHeader() {
      return this.ds.headers;
   }

   private expandAll() {
      let getRowToExpand = () => _.find(this.pageRows, row => row.isExpandable && !row.isExpanded);
      let rowToExpand = getRowToExpand();
      while (rowToExpand) {
         this.expandRow(rowToExpand);
         rowToExpand = getRowToExpand();
      }
   }

   expandRow(rowToExpand) {
      let rowIndex = this.pageRows.indexOf(rowToExpand);
      rowToExpand.isExpanded = !rowToExpand.isExpanded;
      if (rowToExpand.isExpanded) {
         this.pageRows.splice.apply(this.pageRows, [rowIndex + 1, 0].concat(rowToExpand.data));
      } else {
         this.pageRows.splice(rowIndex + 1, rowToExpand.data.length);
      }
   }

   sortBy(field:DatasetField, order:string) {
      field.sort = order;

      if (this.sortFields.indexOf(field) < 0) {
         this.sortFields.push(field);
      }

      return this.doSort().then(() => this.setPageRows());
   }

   removeSort(field:DatasetField) {
      field.sort = null;
      this.sortFields = _.without(this.sortFields, field);
      return this.doSort().then(() => this.setPageRows());
   }

   private getSortFn() {
      let sortFn;

      for (let field of this.sortFields) {
         if (field.sort) {
            if (!sortFn) {
               sortFn = firstBy(field.getSortFn());
            } else {
               sortFn = sortFn.thenBy(field.getSortFn());
            }
         }
      }

      return sortFn;
   }

   private doSort() {
      let deferred = this.$q.defer();
      _.defer(() => {
         this.data.sort(this.getSortFn());
         deferred.resolve();
      });
      return deferred.promise;
   }

   sum(field:DatasetField, data:any[] = null) {
      if (!data && this.data) {
         return this.sum(field, this.data);
      }

      if (!field.sum || !data) {
         return;
      }

      if (field.type == 'number') {
         return _.reduce(data, (total, row) => total + row[field.name], 0);
      }

      if (field.type == 'percent') {
         return _.reduce(data, (total, row) => total + row[field.name], 0) / data.length;
      }

      if (field.type == 'string') {
         return _.keys(_.groupBy(data, field.name)).length;
      }

      if (field.type == 'bool') {
         return _.filter(data, row => row[field.name]).length;
      }
   }

   formatSum(field:DatasetField) {
      let result = this.sum(field);

      if (field.type == 'bool') {
         // bypass format function which uses boolFilter
         return result;
      }

      return this.format(field, result);
   }

   format(field:DatasetField, val) {
      if (ng.isObject(val)) {
         let row = val;
         val = row[field.name];

         if (row.isExpandable && field.hideInParentRows) {
            return '';
         } else if (row.isSubrow && field.hideInSubRows) {
            return '';
         }
      }

      let filterName = field.type + 'Filter', args;

      if (field.type == 'datetime') {
         filterName = 'dateFilter';
         args = 'MMM d, y h:mm a';
      } else if (field.type == 'time') {
         return val ? val.slice(0, 5) : val; // 19:30:00
      } else if (field.type == 'percent') {
         args = 2; // two decimal places
      }

      if (this.$injector.has(filterName)) {
         let filter = this.$injector.get(filterName) as Function;
         return filter(val, args);
      } else {
         return val;
      }
   }

   load(filters) {
      return this.reportsService.data(this.ds.id, filters).then((response) => {
         let data = response.data as any[];

         if (this.ds.groupBy) {
            this.loadGrouped(data);
         } else {
            this.data = _.map(data, row => {
               row.rowId = _.uniqueId();
               return row;
            });
         }

         this.setPageRows();

         return this.data;
      });
   };

   loadGrouped(data:any[]) {
      let grouped:any[] = [];
      let prevRow = {};
      let group;

      for (let row of data) {
         let groupBy = row[this.ds.groupBy.name];

         if (groupBy !== prevRow[this.ds.groupBy.name] || grouped.length == 0) {
            group = ng.copy(row);
            group.data = [];
            group.isExpandable = true;
            group.rowId = _.uniqueId();
            grouped.push(group);
         }

         row.rowId = _.uniqueId();
         group.data.push(row);
         prevRow = row;
      }

      for (let group of grouped) {
         if (group.data.length > 1 || this.ds.type === 'table-expanded') {
            for (let field of this.ds.fields) {
               if (field.sum && (field.type == 'number' || field.type == 'percent')) {
                  group[field.name] = this.sum(field, group.data);
               }
            }

            for (let row of group.data) {
               row.isSubrow = true;
            }
         } else {
            group.isExpandable = false;
            group.data = [];
         }
      }

      this.data = grouped;
   }

   download(format, filters) {
      this.reportsService.download(this.ds.id, filters, format);
   }
}

export interface ReportTableFactory {
   ({ds:Dataset}):ReportTableService;
}
